<template>
  <span class="text-sm">
    <a
      class="dotted-underline"
      v-if="valueF.link"
      :href="valueF.link"
      target="_blank"
      rel="noreferrer noopener"
      >{{ valueF.label }}</a
    >
    <span class="" v-else>{{ valueF.label }}</span>
  </span>
</template>
<script lang="ts" setup>
import type { PlanFeatures, PlanLimits } from '@mailslurp/admin-sdk';
import { PlanAddonsSupportedAddonsEnum } from '@mailslurp/admin-sdk';
import { computed } from 'vue';

import { assertUnreachable } from '../../lib/services/assertUnreachable';

const props = defineProps<{
  limit?: keyof PlanLimits;
  feature?: keyof PlanFeatures;
  addon?: PlanAddonsSupportedAddonsEnum;
}>();
const valueF = computed<{ label: string; link?: string }>(() => {
  if (props.feature) {
    const n: keyof PlanFeatures = props.feature;
    switch (n) {
      case 'inboxForwarders': {
        return { label: 'Email forwarding' };
      }
      case 'inboxRulesets': {
        return { label: 'Routing rules' };
      }
      case 'contacts': {
        return { label: 'Contact management' };
      }
      case 'permanentInbox': {
        return { label: 'Permanent inbox' };
      }
      case 'attachments': {
        return { label: 'Email attachments' };
      }
      case 'trackingPixels': {
        return { label: 'Tracking pixels' };
      }
      case 'inboxRepliers': {
        return { label: 'Auto replies' };
      }
      case 'zapier': {
        return { label: 'Zapier integration' };
      }
      case 'webhooks': {
        return { label: 'Webhooks' };
      }
      case 'password': {
        return { label: 'Password protection' };
      }
      case 'bulk': {
        return { label: 'Bulk operations' };
      }
      case 'samlSSO': {
        return { label: 'SAML SSO' };
      }
      case 'sendWithQueue': {
        return { label: 'Queue-based sending' };
      }
      case 'emailFeatureCheck': {
        return { label: 'Email feature check' };
      }
      case 'phonePlans': {
        return { label: 'Phone/SMS' };
      }
      case 'userPlans': {
        return { label: 'Extra team mates' };
      }
      case 'backups': {
        return { label: 'Email backups' };
      }
      case 'screenshots': {
        return { label: 'Email screenshots' };
      }
      default: {
        assertUnreachable(n);
        return { label: n };
      }
    }
  } else if (props.limit) {
    const n: keyof PlanLimits = props.limit;
    switch (n) {
      case 'recipients': {
        return { label: 'Recipients' };
      }
      case 'domains': {
        return {
          label: 'Domains',
          link: 'https://docs.mailslurp.com/custom-domains/',
        };
      }
      case 'monthlyVerifyEmailAddress': {
        return { label: 'Verifications' };
      }
      case 'monthlyGetMailServerRecords': {
        return { label: 'DNS check' };
      }
      case 'monthlyInboxes': {
        return { label: 'Included inboxes' };
      }
      case 'monthlySend': {
        return { label: 'Included outbound email' };
      }
      case 'monthlyReceive': {
        return { label: 'Included inbound email' };
      }
      case 'phoneNumbers': {
        return { label: 'Phone numbers' };
      }
      case 'members': {
        return { label: 'Included team members' };
      }
      case 'aliases': {
        return { label: 'Proxy alias' };
      }
      case 'threads': {
        return { label: 'Proxy replies' };
      }
      case 'organizations': {
        return { label: 'Included team members' };
      }
      case 'maxUniqueRecipients': {
        return { label: 'Message recipients' };
      }
      default: {
        return assertUnreachable(n);
      }
    }
  } else if (props.addon) {
    const n: PlanAddonsSupportedAddonsEnum = props.addon;
    switch (n) {
      case PlanAddonsSupportedAddonsEnum.GUEST: {
        return { label: 'Guest access' };
      }
      case PlanAddonsSupportedAddonsEnum.EMAILS:
        return { label: 'Extra emails' };
      case PlanAddonsSupportedAddonsEnum.INBOXES:
        return { label: 'Extra inboxes' };
      case PlanAddonsSupportedAddonsEnum.AI:
        return { label: 'AI tokens' };
      case PlanAddonsSupportedAddonsEnum.CONNECTORS:
        return { label: 'External inboxes' };
      case PlanAddonsSupportedAddonsEnum.VALIDATION:
        return { label: 'Email verification' };
      default: {
        return assertUnreachable(n);
      }
    }
  } else {
    return { label: 'Unknown' };
  }
});
</script>
